import { useState, useCallback } from 'react';

/**
 * ジャンプボタンの種別を表す定数
 */
export const JUMP_BUTTON_TYPES = {
  DISEASE: 'disease',    // 該当傷病
  CONTENTS: 'contents',  // 目次
  LEGEND: 'legend',      // 凡例
  APPENDIX: 'appendix',  // 別表
};

/**
 * ジャンプボタングループを管理するカスタムフック
 * 
 * PDFビューア内の該当傷病、目次、凡例、別表へのジャンプ機能を提供する。
 * 各ボタンのアクティブ状態とジャンプ可否を管理し、クリック時のハンドラを返す。
 * 
 * @param {Object} options - オプション
 * @param {Object} options.detailResult - 検索結果の詳細データ（各セクションのページ番号を含む）
 * @param {Function} options.jumpToPage - 指定ページへジャンプする関数
 * @param {Function} options.isJumpPageValid - ジャンプ可否を判定する関数
 * @returns {Object} ジャンプボタン関連の状態とハンドラ
 * @returns {string} returns.activeJumpButton - 現在アクティブなボタン種別（'disease' | 'contents' | 'legend' | 'appendix'）
 * @returns {boolean} returns.canJumpDisease - 該当傷病へジャンプ可能か
 * @returns {boolean} returns.canJumpContents - 目次へジャンプ可能か
 * @returns {boolean} returns.canJumpLegend - 凡例へジャンプ可能か
 * @returns {boolean} returns.canJumpAppendix - 別表へジャンプ可能か
 * @returns {Function} returns.handleJumpDisease - 該当傷病へジャンプするハンドラ
 * @returns {Function} returns.handleJumpContents - 目次へジャンプするハンドラ
 * @returns {Function} returns.handleJumpLegends - 凡例へジャンプするハンドラ
 * @returns {Function} returns.handleJumpAppendix - 別表へジャンプするハンドラ
 */
export const useJumpButtonGroup = ({ detailResult, jumpToPage, isJumpPageValid }) => {
  /**
   * 現在アクティブなジャンプボタン
   * JUMP_BUTTON_TYPES の値を使用
   */
  const [activeJumpButton, setActiveJumpButton] = useState(JUMP_BUTTON_TYPES.DISEASE);

  /**
   * 各セクションへのジャンプ可否
   * detailResultに該当ページ番号が存在するかで判定
   */
  const canJumpDisease = isJumpPageValid(detailResult, 'pages');
  const canJumpContents = isJumpPageValid(detailResult, 'tableOfContents');
  const canJumpLegend = isJumpPageValid(detailResult, 'legends');
  const canJumpAppendix = isJumpPageValid(detailResult, 'appendixTables');

  /**
   * ジャンプ実行の共通処理（内部用）
   * アクティブボタンを更新し、指定ページへジャンプする
   * 
   * @param {string} buttonType - ボタン種別（JUMP_BUTTON_TYPES の値）
   * @param {number} pageValue - ジャンプ先のページ番号
   */
  const executeJump = useCallback((buttonType, pageValue) => {
    if (!detailResult) return;
    setActiveJumpButton(buttonType);
    jumpToPage(pageValue);
  }, [detailResult, jumpToPage]);

  /** 該当傷病へジャンプ */
  const handleJumpDisease = useCallback(() => {
    executeJump(JUMP_BUTTON_TYPES.DISEASE, detailResult?.pages);
  }, [executeJump, detailResult]);

  /** 目次へジャンプ */
  const handleJumpContents = useCallback(() => {
    executeJump(JUMP_BUTTON_TYPES.CONTENTS, detailResult?.tableOfContents);
  }, [executeJump, detailResult]);

  /** 凡例へジャンプ */
  const handleJumpLegends = useCallback(() => {
    executeJump(JUMP_BUTTON_TYPES.LEGEND, detailResult?.legends);
  }, [executeJump, detailResult]);

  /** 別表へジャンプ */
  const handleJumpAppendix = useCallback(() => {
    executeJump(JUMP_BUTTON_TYPES.APPENDIX, detailResult?.appendixTables);
  }, [executeJump, detailResult]);

  return {
    // 状態
    activeJumpButton,
    canJumpDisease,
    canJumpContents,
    canJumpLegend,
    canJumpAppendix,
    // ハンドラ
    handleJumpDisease,
    handleJumpContents,
    handleJumpLegends,
    handleJumpAppendix,
  };
};

export default useJumpButtonGroup;
